import { ReactNode } from 'react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { useTranslation } from '@frontend/i18n';
import { AssociatedContactsListItem } from './associated-contacts-list-item';
import { ContactsDrawerHeader } from './atoms/contacts-drawer-header';
import { type ContactListItemVariant } from './contact-list-item';
import { contactsListStyles } from './contacts-drawer.styles';
import { ThreadPopoverDrawer, type ThreadPopoverDrawerProps } from './thread-popover-drawer';

export type DrawerContact = {
  personId: string;
  person?: Person;
  isPrimary?: boolean;
};

type ContactsDrawerBaseProps = Omit<ThreadPopoverDrawerProps, 'children' | 'closeLabel' | 'open' | 'onClose'> & {
  contacts: DrawerContact[];
  title: string;
  subtitle?: string;
  count?: number;
  open: boolean;
  onClose: () => void;
  onSelect: (personId: string) => void;
  onSetPrimaryContact?: (personId: string) => void;
  locationId: string;
  rightSlot?: ReactNode;
  variant?: ContactListItemVariant;
};

export const ContactsDrawerBase = ({
  contacts,
  title,
  subtitle,
  count,
  open,
  onClose,
  onSelect,
  onSetPrimaryContact,
  locationId,
  rightSlot,
  variant,
  ...rest
}: ContactsDrawerBaseProps) => {
  const { t } = useTranslation('thread-header');

  return (
    <ThreadPopoverDrawer open={open} onClose={onClose} closeLabel={t('Close')} {...rest}>
      <ContactsDrawerHeader title={title} subtitle={subtitle} count={count ?? contacts.length} onClose={onClose} />
      <ul css={contactsListStyles}>
        {contacts.map((contact) => (
          <AssociatedContactsListItem
            key={contact.personId}
            personId={contact.personId}
            person={contact.person}
            onSelect={() => {
              onSelect(contact.personId);
              onClose();
            }}
            onSetPrimaryContact={
              onSetPrimaryContact
                ? () => {
                    onSetPrimaryContact(contact.personId);
                    onClose();
                  }
                : undefined
            }
            isPrimary={contact.isPrimary}
            rightSlot={rightSlot}
            locationId={locationId}
            size='small'
            variant={variant}
          />
        ))}
      </ul>
    </ThreadPopoverDrawer>
  );
};
